import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Box, Stack, Typography } from '@mui/material';
import { AddOutlined } from '@mui/icons-material';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  type FieldArrayRenderProps,
  useFormik,
  FieldArray,
  FormikProvider,
  type FormikErrors,
} from 'formik';
import { object, string, array, date, boolean, lazy } from 'yup';
import { AccountCodes } from './AccountCodes';
import {
  type FundingPlan,
  BonusType,
  fundingDeclarationMappings,
  FundingSource,
  ProjectRole,
} from './types';
import { roleOptions } from './options';
import type { FundingsValue, FundingMappings, FundingDeclaration } from '../';
import { PAGE_TEXT } from '../../lang';
import {
  type MappingType,
  FUNDING_TAGS,
  fundingValidTags,
} from '../../constant';
import {
  Select,
  Button,
  Checkbox,
  TextField,
  FILE_MANAGE_ORDER,
  type FileData,
  type AlertDetails,
  UploadBox,
  Alert,
  type SelectOption,
  IconButton,
  Dialog,
  DisplayFileData,
} from '../../../../../components';
import { handleReadyNominationSubmit } from '../../../../../utils';
import { AssessmentAccordion } from '../../../../Assessment';
import { FUNDING_STATUS } from '../../../../ScholarshipManagement/constants/fundingStatus';
import { type ProgrammeTypes } from '../../../constants';
dayjs.extend(isSameOrBefore);

type FundingSourceBoxProps = {
  title: string;
  applicationNo: string;
  initialFundingValues: FundingsValue;
  isViewMode: boolean;
  isFourYearAllocation: boolean;
  isPolyuCompetitive: boolean;
  programmeInGsb: ProgrammeTypes;
  departmentOptions: SelectOption[];
  fundingMapping: FundingMappings;
  handleSubmit: (
    values: FundingsValue,
    configTags: FUNDING_TAGS[],
  ) => Promise<void>;
};

const bonusLabels = PAGE_TEXT.FUNDING_SOURCE.BONUS.VALUES;

const FUNDING_TEXT = PAGE_TEXT.FUNDING_SOURCE;
const hintObj = FUNDING_TEXT.PROOF_OF_INCENTIVE_CATEGORIES.HINTS;
const hintArr = Object.values(hintObj);

export const getTagsByFundingSource = (
  fundingMappings: FundingMappings,
  schema: MappingType | '',
  source: string,
): FUNDING_TAGS[] => {
  if (schema === '') return [];
  return fundingMappings[schema].flatMap(item => {
    const tags = item.value === source ? item.tags : [];
    return [...tags];
  });
};

export const needMakeupStatus = [
  FUNDING_STATUS.SUCCESSFUL_EXCEPTIONALLY_GOOD_LIST,
  FUNDING_STATUS.SUCCESSFUL_VERY_GOOD_LIST,
  FUNDING_STATUS.SUCCESSFUL_GOOD_LIST,
];
export const bonusFields: BonusType[] = Object.values(BonusType);
export const bonusLabelArr = Object.values(bonusLabels);
export const FundingSourceBox: React.FunctionComponent<
  FundingSourceBoxProps
> = (props: FundingSourceBoxProps) => {
  const {
    title,
    initialFundingValues,
    fundingMapping,
    isFourYearAllocation,
    departmentOptions,
    isViewMode,
    isPolyuCompetitive,
    handleSubmit,
    applicationNo,
  } = props;

  const isUniversityDual =
    initialFundingValues.source === FundingSource.DUAL_PHD_DEGREE_PROGRAMME;

  const specialPanelCase = isPolyuCompetitive || isUniversityDual;

  // 2025/03/03
  const makeupAccountCodeMode =
    specialPanelCase &&
    needMakeupStatus.some(sta => initialFundingValues.status === sta);

  const { proofOfScheme = [] } = initialFundingValues;
  const [configTags, setConfigTags] = useState<FUNDING_TAGS[]>([]);
  const [dialogOpen, setDialogOpen] = useState({ open: false, msg: '' });
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [proofOfIncentives, setProofOfIncentives] =
    useState<FileData[]>(proofOfScheme);

  const fundingPlanSchema = object({
    accountCode: string().required('Required'),
    period: object({
      from: date().required('Required'),
      to: date().required('Required'),
    }),
  });

  const formik = useFormik<FundingsValue>({
    initialValues: initialFundingValues,
    validationSchema: object({
      schema: string().required('Required'),
      source: string().required('Required'),

      fundingPlan: lazy((value, { parent }) => {
        const { sufficientFundingConfirm } = parent;
        return ((sufficientFundingConfirm as boolean) ||
          makeupAccountCodeMode) &&
          needAccountCode
          ? array().of(fundingPlanSchema)
          : array().of(object());
      }),
      sufficientFundingConfirm: boolean(),
      stipendBudgetCreationConfirm: boolean().when(() => {
        return makeupAccountCodeMode ? boolean().oneOf([true]) : boolean();
      }),
      externalGrantsConfirm: boolean().when(() => {
        return makeupAccountCodeMode ? boolean().oneOf([true]) : boolean();
      }),
      accountInfoAccurateConfirm: boolean().when(() => {
        return makeupAccountCodeMode ? boolean().oneOf([true]) : boolean();
      }),

      projectInformation: object().when(() => {
        return needProjRole
          ? object({
              role: string().required('Required'),
              PIName: string().when('role', ([role], schema) => {
                return role === ProjectRole.CI
                  ? schema.required('Required')
                  : schema.notRequired();
              }),
              PIDepartment: string().when('role', ([role], schema) => {
                return role === ProjectRole.CI
                  ? schema.required('Required')
                  : schema.notRequired();
              }),
              justification: string().when('role', ([role], schema) => {
                return role === ProjectRole.CI
                  ? schema.required('Required')
                  : schema.notRequired();
              }),
            })
          : object();
      }),
      declaration: lazy((value, { parent }) => {
        const { source } = parent;
        return source in fundingDeclarationMappings
          ? array().of(
              object({
                declaration: string(),
                confirmed: boolean().oneOf([true]),
              }),
            )
          : array().of(object());
      }),
    }),
    enableReinitialize: true,
    onSubmit: async (values: FundingsValue): Promise<void> => {
      if (needProofDocs && proofOfIncentives.length === 0) {
        setAlertDetails({
          alertType: 'warning',
          alertContent:
            'Please upload Proof of Incentive Scheme & Strategic Allocation Categories.',
        });
        setOpenAlert(true);
        return;
      }
      if (needAccountCode) {
        const check = checkContinuous12Months(values.fundingPlan);

        if (
          (values.sufficientFundingConfirm || makeupAccountCodeMode) &&
          !check.valid
        ) {
          setDialogOpen({
            open: true,
            msg: PAGE_TEXT.FUNDING_SOURCE.CHECK_1,
          });
          return;
        }
        // close this block for temp 2025/02/25

        // const programmeDurationYear = fullTimeDurationMap[programmeInGsb] ?? 0;
        // if (
        //   makeupAccountCodeMode &&
        //   check.durationYear + (values.supportYears ?? 0) <
        //     programmeDurationYear
        // ) {
        //   setDialogOpen({
        //     open: true,
        //     msg: PAGE_TEXT.FUNDING_SOURCE.CHECK_2,
        //   });
        //   return;
        // }
      }

      const fundingValues: FundingsValue = {
        ...values,
        proofOfScheme: proofOfIncentives,
      };
      await handleSubmit(fundingValues, configTags);
    },
  });
  const handleDialogConfirmSave = async (): Promise<void> => {
    const fundingValues: FundingsValue = {
      ...formik.values,
      proofOfScheme: proofOfIncentives,
    };
    await handleSubmit(fundingValues, configTags);
    setDialogOpen({ open: false, msg: '' });
  };

  useEffect(() => {
    const { schema, source } = initialFundingValues;
    const updateTags = getTagsByFundingSource(
      fundingMapping,
      (schema as MappingType) ?? '',
      source,
    );
    setConfigTags(updateTags);
    formik.resetForm({
      values: {
        ...initialFundingValues,
      },
    });
    setProofOfIncentives(initialFundingValues.proofOfScheme ?? []);
  }, [initialFundingValues]);

  function handleProofOfIncentiveChange(
    order: number,
    file: FileData,
  ): boolean {
    let newProofOfIncentives = [...proofOfIncentives];
    const processingFileName = `${applicationNo}_proofs_letter_${
      proofOfIncentives.length + 1
    }`;
    switch (order) {
      case FILE_MANAGE_ORDER.UPLOAD:
        newProofOfIncentives = [
          ...newProofOfIncentives,
          {
            id: file.id,
            link: file.link,
            name: processingFileName,
          },
        ];
        break;

      case FILE_MANAGE_ORDER.REMOVE:
        if (
          typeof file.id === 'string' &&
          newProofOfIncentives.some(({ id }) => id === file.id)
        ) {
          newProofOfIncentives = newProofOfIncentives.filter(
            ({ id }) => id !== file.id,
          );
        }
        break;
      default:
    }
    setProofOfIncentives(newProofOfIncentives);
    return true;
  }

  function checkContinuous12Months(fundingPlan: FundingPlan[]): {
    valid: boolean;
    durationYear: number;
  } {
    const periods = fundingPlan
      .map(plan => ({
        from: dayjs(plan.period.from).startOf('month'),
        to: dayjs(plan.period.to).endOf('month'),
      }))
      .sort((a, b) => a.from.unix() - b.from.unix());

    const merged: Array<{ from: dayjs.Dayjs; to: dayjs.Dayjs }> = [];
    for (const period of periods) {
      const last = merged[merged.length - 1];
      if (last && period.from.isSameOrBefore(last.to.add(1, 'day'))) {
        last.to = period.to.isAfter(last.to) ? period.to : last.to;
      } else {
        merged.push({ ...period });
      }
    }

    let valid = false;
    let totalMonths = 0;

    for (const period of merged) {
      const months = period.to.diff(period.from, 'month') + 1;
      totalMonths += months;
      if (months >= 12) valid = true;
    }

    if (!valid) {
      valid = merged.length === 1 && totalMonths >= 12;
    }

    return {
      valid,
      durationYear: Math.floor(totalMonths / 12),
    };
  }
  const removeFundingPlan =
    (arrayHelpers: FieldArrayRenderProps, index: number): (() => void) =>
    () => {
      arrayHelpers.remove(index);
    };

  const addFundingPlan =
    (arrayHelpers: FieldArrayRenderProps) =>
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      arrayHelpers.push({
        accountCode: '',
        period: { from: '', to: '' },
      });
    };

  const handleProjRoleChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    const newRole = event.target.value as ProjectRole;
    if (newRole === formik.values.projectInformation?.role) return;
    await formik.setFieldValue('projectInformation', {
      role: newRole,
      PIName: '',
      PIDepartment: null,
      justification: '',
    });
    // when the role is PI, supervisor do not need to tick the declaration checkbox
    await formik.setFieldValue(
      'declaration[0].confirmed',
      newRole === ProjectRole.PI,
    );
  };

  const needDeclaration =
    initialFundingValues.source in fundingDeclarationMappings;

  const needOptionalAccountCode =
    initialFundingValues.source === FundingSource.SELF_FINANCED;

  const nothingRequired =
    !configTags.some(tag => fundingValidTags.includes(tag)) &&
    !needDeclaration &&
    !needOptionalAccountCode;

  const needProofDocs = [
    FUNDING_TAGS.NEED_PROOF_GS_EMAIL,
    FUNDING_TAGS.NEED_PROOF_RIO_EMAIL,
    FUNDING_TAGS.NEED_PROOF_RESEARCH_UNIT_EMAIL,
    FUNDING_TAGS.NEED_PROOF_PROJECT_RECORD,
  ].some(tag => configTags.includes(tag));

  // RPG-[1232] hide account code section for PolyU Competitive Funding
  const needAccountCode =
    isFourYearAllocation ||
    (configTags.includes(FUNDING_TAGS.NEED_ACCOUNT) &&
      (!isPolyuCompetitive ||
        (isPolyuCompetitive &&
          needMakeupStatus.some(
            sta => initialFundingValues.status === sta,
          )))) ||
    needOptionalAccountCode;

  const needProjRole = configTags.includes(FUNDING_TAGS.NEED_PROJECT_ROLE);
  const needProjRoleCIInfo =
    formik.values.projectInformation.role === ProjectRole.CI;

  const handleCloseDialog = (): void => {
    setDialogOpen({ open: false, msg: '' });
  };

  const generalDeclarationBox = (): React.ReactNode => {
    const boxLabel =
      fundingDeclarationMappings[
        initialFundingValues.source as FundingSource
      ] ?? '';

    if (!boxLabel) return <></>;
    const declarationError = formik.errors
      ?.declaration?.[0] as FormikErrors<FundingDeclaration>;
    return (
      <div>
        <hr className="pt-[24px]" />
        <Checkbox
          id="declaration"
          name="declaration[0].confirmed"
          className="mb-2"
          onChange={formik.handleChange}
          label={<div style={{ marginTop: '0.65em' }}>{boxLabel}</div>}
          error={
            formik.touched.declaration?.[0]?.confirmed &&
            Boolean(declarationError?.confirmed)
          }
          disabled={isViewMode}
          value={formik.values.declaration?.[0]?.confirmed ?? false}
          sx={{
            alignItems: 'flex-start',
          }}
        />
      </div>
    );
  };

  const makeupDeclaration: Array<{ name: keyof FundingsValue; label: string }> =
    [
      {
        name: 'stipendBudgetCreationConfirm',
        label: FUNDING_TEXT.ACCOUNT.RIO_CONFIRM,
      },
      {
        name: 'externalGrantsConfirm',
        label: FUNDING_TEXT.ACCOUNT.EXTERNAL_GRANT_CONFIRM,
      },
      {
        name: 'accountInfoAccurateConfirm',
        label: FUNDING_TEXT.ACCOUNT.INFO_ACCURATE_CONFIRM,
      },
    ];
  const handleDisplayProjRoleBox: React.ReactNode = (
    <>
      <h3 className="font-bold mb-2 mt-8" id="projectInformation">
        {FUNDING_TEXT.ROLE.LABEL}
      </h3>
      <div className="flex items-center mb-4">
        <div className="mr-4">{FUNDING_TEXT.ROLE.MY_ROLE}</div>
        <Select
          name="projectInformation.role"
          className={'w-1/5'}
          options={roleOptions}
          required
          disabled={isViewMode}
          InputLabelProps={{ shrink: true }}
          onChange={handleProjRoleChange}
          onBlur={formik.handleBlur}
          value={formik.values.projectInformation.role ?? ''}
          error={
            Boolean(formik.touched.projectInformation?.role) &&
            Boolean(formik.errors.projectInformation?.role)
          }
          helperText={
            formik.touched.projectInformation?.role &&
            formik.errors.projectInformation?.role
          }
        />
      </div>
    </>
  );

  return (
    <>
      {nothingRequired ? (
        <div className="font-bold bg-[#F6F6F6] px-[3.25rem] py-4">{title}</div>
      ) : (
        <AssessmentAccordion
          title={title}
          className="!bg-[#F6F6F6]"
          defaultExpanded
          disableExpand
          AssessDetails={
            <div className="!px-4 z-40 bg-white">
              <Alert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alertDetails={alertDetails}
              />
              <Dialog
                openDialog={dialogOpen.open}
                width={'700px'}
                dialogTitle={''}
                cancelAction={PAGE_TEXT.FUNDING_SOURCE.CHECK_AGAIN}
                onDialogAction={handleDialogConfirmSave}
                onDialogClose={handleCloseDialog}
              >
                <Typography margin={'0rem 1.5rem 2rem'}>
                  {dialogOpen.msg}
                </Typography>
              </Dialog>

              <FormikProvider value={formik}>
                <form
                  onSubmit={handleReadyNominationSubmit(formik)}
                  noValidate
                  style={{
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                >
                  <Stack>
                    <Box padding={2}>
                      {/* FUNDING ACCOUNTS : BOXES WITH ACCOUNT NO AND DATES */}
                      {needAccountCode && (
                        <FieldArray
                          name="fundingPlan"
                          render={(arrayHelpers: FieldArrayRenderProps) => (
                            <>
                              <div
                                className="flex flex-row content-center mb-4 gap-2"
                                id="fundingPlan"
                              >
                                <h3 className="font-bold">
                                  {FUNDING_TEXT.ACCOUNT.LABEL}
                                </h3>
                                {!isViewMode && (
                                  <IconButton
                                    className="!pr-2 !mt-[0.1em]"
                                    onClick={addFundingPlan(arrayHelpers)}
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      backgroundColor: '#951D1D',
                                      color: 'white',
                                      ':hover': {
                                        backgroundColor: '#951D1D',
                                      },
                                    }}
                                    icon={<AddOutlined />}
                                  />
                                )}
                              </div>
                              {!needOptionalAccountCode && (
                                <h2 className="mb-2 text-red-500 italic">
                                  {FUNDING_TEXT.ACCOUNT.HINT}
                                </h2>
                              )}
                              {makeupAccountCodeMode && (
                                <>
                                  <div
                                    className="flex-wrap mb-2 gap-2 text-wrap"
                                    style={{ overflowWrap: 'break-word' }}
                                  >
                                    {FUNDING_TEXT.ACCOUNT.PANEL.ONE}&nbsp;
                                    <b className="underline">
                                      {initialFundingValues.supportYears}
                                    </b>
                                    &nbsp;
                                    {FUNDING_TEXT.ACCOUNT.PANEL.TWO}
                                  </div>
                                  <div className="my-4">
                                    {FUNDING_TEXT.ACCOUNT.MAKEUP_HINT}
                                  </div>
                                </>
                              )}
                              {formik.values?.fundingPlan?.map(
                                (item: FundingPlan, index) => {
                                  return (
                                    <AccountCodes
                                      key={`fundingPlan${index}`}
                                      index={index}
                                      isViewMode={isViewMode}
                                      handleDeleteFundingAccount={removeFundingPlan(
                                        arrayHelpers,
                                        index,
                                      )}
                                    />
                                  );
                                },
                              )}

                              {makeupAccountCodeMode ? (
                                <div className="p-4">
                                  {makeupDeclaration.map((d, index) => (
                                    <Checkbox
                                      key={index}
                                      name={d.name}
                                      sx={{
                                        alignItems: 'flex-start',
                                      }}
                                      disabled={isViewMode}
                                      label={
                                        <div
                                          className="!mt-[0.55em]"
                                          dangerouslySetInnerHTML={{
                                            __html: d.label,
                                          }}
                                        />
                                      }
                                      onChange={formik.handleChange}
                                      value={formik.values?.[d.name] as boolean}
                                      error={
                                        formik.touched?.[d.name] &&
                                        Boolean(formik.errors?.[d.name])
                                      }
                                    />
                                  ))}
                                </div>
                              ) : (
                                <>
                                  {!needOptionalAccountCode && (
                                    <div className="p-4">
                                      <Checkbox
                                        name="sufficientFundingConfirm"
                                        sx={{
                                          alignItems: 'flex-start',
                                        }}
                                        disabled={isViewMode}
                                        label={
                                          <div className="!mt-[0.55em]">
                                            {FUNDING_TEXT.ACCOUNT.CONFIRM}
                                          </div>
                                        }
                                        onChange={formik.handleChange}
                                        value={
                                          formik.values
                                            .sufficientFundingConfirm ?? false
                                        }
                                        error={
                                          formik.touched
                                            .sufficientFundingConfirm &&
                                          Boolean(
                                            formik.errors
                                              .sufficientFundingConfirm,
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        />
                      )}

                      {/* RPG-[1796]  when its to make up the account code after panel, hide declaration from supervisors, show in monitor */}

                      {specialPanelCase &&
                        (!makeupAccountCodeMode || isViewMode) && (
                          <div className="p-4">{generalDeclarationBox()}</div>
                        )}

                      {/* INCENTIVE GRE/ECS */}
                      {needProjRole && (
                        <div>
                          {handleDisplayProjRoleBox}
                          <div>
                            {/* ROLE = CI */}
                            {needProjRoleCIInfo && (
                              <div>
                                {generalDeclarationBox()}
                                <div className="italic text-[#951D1D] mb-8">
                                  {FUNDING_TEXT.ROLE.CI.DESC}
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                  <TextField
                                    name="projectInformation.PIName"
                                    label={FUNDING_TEXT.ROLE.PI.NAME}
                                    required
                                    value={
                                      formik.values.projectInformation.PIName
                                    }
                                    disabled={isViewMode}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                      formik.touched.projectInformation
                                        ?.PIName &&
                                      Boolean(
                                        formik.errors.projectInformation
                                          ?.PIName,
                                      )
                                    }
                                    helperText={
                                      formik.touched.projectInformation
                                        ?.PIName &&
                                      formik.errors.projectInformation?.PIName
                                    }
                                  />
                                  <Select
                                    name="projectInformation.PIDepartment"
                                    required
                                    options={departmentOptions}
                                    label={FUNDING_TEXT.ROLE.PI.DEPARTMENT}
                                    value={
                                      formik.values.projectInformation
                                        .PIDepartment ?? ''
                                    }
                                    disabled={isViewMode}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                      formik.touched.projectInformation
                                        ?.PIDepartment &&
                                      Boolean(
                                        formik.errors.projectInformation
                                          ?.PIDepartment,
                                      )
                                    }
                                    helperText={
                                      formik.touched.projectInformation
                                        ?.PIDepartment &&
                                      formik.errors.projectInformation
                                        ?.PIDepartment
                                    }
                                  />
                                </div>

                                <TextField
                                  required
                                  name="projectInformation.justification"
                                  label={FUNDING_TEXT.ROLE.PI.JUSTIFICATION}
                                  className="w-full !mb-6"
                                  multiline
                                  disabled={isViewMode}
                                  minRows={4}
                                  value={
                                    formik.values.projectInformation
                                      .justification
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={
                                    formik.touched.projectInformation
                                      ?.justification &&
                                    Boolean(
                                      formik.errors.projectInformation
                                        ?.justification,
                                    )
                                  }
                                  helperText={
                                    formik.touched.projectInformation
                                      ?.justification &&
                                    formik.errors.projectInformation
                                      ?.justification
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {needProofDocs && (
                        <div>
                          <h3 className="font-bold mb-2 mt-8">
                            {FUNDING_TEXT.PROOF_OF_INCENTIVE_CATEGORIES.TITLE}
                          </h3>
                          <div className="ml-2 mb-8">
                            {hintArr.map((item, index) => {
                              return (
                                <div
                                  key={index}
                                >{`${index + 1}.  ${item}`}</div>
                              );
                            })}
                          </div>

                          {!isViewMode ? (
                            <div className="w-4/5">
                              <UploadBox
                                maximumNum={5}
                                files={proofOfIncentives}
                                handleFilesChange={handleProofOfIncentiveChange}
                              />
                            </div>
                          ) : (
                            <div className="px-4">
                              <DisplayFileData fileData={proofOfIncentives} />
                            </div>
                          )}
                        </div>
                      )}
                    </Box>

                    <Box textAlign={'center'} marginY={5}>
                      {!isViewMode && (
                        <Button variant="contained" type="submit">
                          {PAGE_TEXT.SAVE}
                        </Button>
                      )}
                    </Box>
                  </Stack>
                </form>
              </FormikProvider>
            </div>
          }
        />
      )}
    </>
  );
};
